import { PeriodMode } from '../../../enums/periodMode'
import { SortDirection } from '../../../enums/sortDirection'
import { useQueryParams, UseQueryParamsResponse } from '../../../hooks/useQueryParams'
import { BankTransactionSortProperty } from '../enums/bankTransactionSortProperty'
import { BankTransactionStatusFilter } from '../enums/bankTransactionStatusFilter'
import { BankTransactionsFilters } from '../types/bankTransactionsFilters'

interface UseBankTransactionsFiltersOverrides {
  /**
   * Pass the sandbox's own filter state (from useSandboxModeContext()) when sandboxed, instead of
   * this hook's default URL-backed state — the sandbox pane overlays /bank-accounts, so it must
   * not read/write bank-transaction filter params on that URL, and every call site needs to share
   * the same filter values rather than each getting independent local state.
   */
  sandboxFilters?: BankTransactionsFilters
  setSandboxFilters?: UseQueryParamsResponse<BankTransactionsFilters>[1]
}

export const defaultBankTransactionsQuery: BankTransactionsFilters = {
  accountId: '',
  transactionTypeFilter: undefined,
  minAmount: undefined,
  maxAmount: undefined,
  period: PeriodMode.All,
  searchQuery: undefined,
  sortDirection: SortDirection.Desc,
  sortProperty: BankTransactionSortProperty.EntryDate,
  statusFilter: BankTransactionStatusFilter.All,
}

const isReconciledByStatus: Record<BankTransactionStatusFilter, boolean | undefined> = {
  [BankTransactionStatusFilter.All]: undefined,
  [BankTransactionStatusFilter.ToReview]: false,
  [BankTransactionStatusFilter.Reviewed]: true,
}

export const useBankTransactionsFilters = (overrides?: UseBankTransactionsFiltersOverrides) => {
  const [urlFilters, setUrlFilters] = useQueryParams<BankTransactionsFilters>({
    defaultQuery: defaultBankTransactionsQuery,
    enabled: !overrides?.sandboxFilters,
  })

  const filters = overrides?.sandboxFilters ?? urlFilters
  const setFilters = overrides?.setSandboxFilters ?? setUrlFilters

  const statusFilter = Object.hasOwn(isReconciledByStatus, filters.statusFilter)
    ? filters.statusFilter
    : BankTransactionStatusFilter.All

  return [
    {
      ...filters,
      isReconciled: isReconciledByStatus[statusFilter],
      statusFilter,
    },
    setFilters,
  ] as const
}
